import { createContext, useContext, PropsWithChildren } from 'react';
import { Socket } from 'socket.io-client';

import { useResearchAssistantSocketInit } from '../hooks/useResearchAssistantSocketInit';
import {
  useResearchAssistantListeners,
  type ResearchAssistantListenersProps,
} from '../namespaces/listeners/useResearchAssistantListeners';

interface ResearchAssistantSocketContextType {
  socket?: Socket;
  cleanupSocket: () => void;
}

export type ResearchAssistantSocketProviderProps = ResearchAssistantListenersProps;

const ResearchAssistantSocketContext = createContext<ResearchAssistantSocketContextType | null>(
  null
);

// Null-render child so listener hooks run inside the context boundary.
const ResearchAssistantWebSocketEffects = (props: ResearchAssistantListenersProps) => {
  useResearchAssistantListeners(props);
  return null;
};

export const ResearchAssistantSocketProvider = ({
  children,
  ...props
}: PropsWithChildren<ResearchAssistantSocketProviderProps>) => {
  const { socket, cleanupSocket } = useResearchAssistantSocketInit();

  return (
    <ResearchAssistantSocketContext.Provider value={{ socket, cleanupSocket }}>
      <ResearchAssistantWebSocketEffects {...props} />
      {children}
    </ResearchAssistantSocketContext.Provider>
  );
};

const noopCleanup = () => {
  /* no provider – nothing to clean up */
};

export const useResearchAssistantSocketContext = (): ResearchAssistantSocketContextType => {
  const context = useContext(ResearchAssistantSocketContext);
  if (!context) {
    return { socket: undefined, cleanupSocket: noopCleanup };
  }
  return context;
};
